import { api } from './api';
import type {
  GuidedOrder,
  GuidedOrderListResponse,
  GuidedOrderSingleResponse,
  GuidedOrderCancelResponse,
} from '@/types/guidedOrder';

export interface ListGuidedOrdersFilters {
  clinic_id?: string;
  patient_id?: string;
  status?: string | string[];
  payment_status?: string | string[];
  fulfillment_source?: 'clinic' | 'nimbus';
  is_subscription?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

class GuidedOrderService {
  /**
   * Get a single guided order by ID with relations
   */
  async getOrder(orderId: string): Promise<GuidedOrderSingleResponse> {
    const response = await api.get<GuidedOrderSingleResponse>(`/guided-orders/${orderId}`);
    return response;
  }

  /**
   * List guided orders with optional filters
   */
  async listOrders(filters: ListGuidedOrdersFilters = {}): Promise<GuidedOrderListResponse> {
    const params = new URLSearchParams();
    if (filters.clinic_id) params.set('clinic_id', filters.clinic_id);
    if (filters.patient_id) params.set('patient_id', filters.patient_id);
    if (filters.status) {
      const status = Array.isArray(filters.status) ? filters.status : [filters.status];
      status.forEach(s => params.append('status', s));
    }
    if (filters.payment_status) {
      const ps = Array.isArray(filters.payment_status) ? filters.payment_status : [filters.payment_status];
      ps.forEach(s => params.append('payment_status', s));
    }
    if (filters.is_subscription != null) params.set('is_subscription', filters.is_subscription);
    if (filters.fulfillment_source) params.set('fulfillment_source', filters.fulfillment_source);
    if (filters.search) params.set('search', filters.search);
    if (filters.limit != null) params.set('limit', String(filters.limit));
    if (filters.offset != null) params.set('offset', String(filters.offset));
    const query = params.toString();
    const url = query ? `/guided-orders?${query}` : '/guided-orders';
    const response = await api.get<GuidedOrderListResponse>(url);
    return response;
  }

  /**
   * Get guided order by prescription ID (checkout or MDI prescription)
   */
  async getByPrescription(prescriptionId: string): Promise<GuidedOrderSingleResponse> {
    const response = await api.get<GuidedOrderSingleResponse>(
      `/guided-orders/prescription/${prescriptionId}`
    );
    return response;
  }

  /**
   * Request cancellation of a guided order (goes to super admin for approval)
   */
  async cancelOrder(orderId: string, reason: string): Promise<GuidedOrderCancelResponse> {
    const response = await api.post<GuidedOrderCancelResponse>(
      `/guided-orders/${orderId}/cancel-request`,
      { reason }
    );
    return response;
  }

  /**
   * Resend amendment consent email/SMS to patient
   */
  async resendAmendmentConsent(orderId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      `/guided-orders/${orderId}/resend-amendment-consent`
    );
    return response;
  }

  /**
   * Assign / change / clear the referrer on a guided order (clinic admin).
   * Only allowed while payment_status is not 'captured'.
   */
  async updateReferrer(
    orderId: string,
    data: {
      referring_user_id: string | null;
      commission_rate_bps?: number | null;
    }
  ): Promise<{
    success: boolean;
    data: {
      order_id: string;
      referring_user_id: string | null;
      commission_rate_bps: number | null;
      referrer: {
        user_id: string;
        name: string;
        email: string;
        primary_role: string;
      } | null;
    };
  }> {
    return await api.patch(`/guided-orders/${orderId}/referrer`, data);
  }
}

export const guidedOrderService = new GuidedOrderService();
export default guidedOrderService;
